<template>
  <div>
    <el-row>
      <el-col :span="24">
        <el-card class="data" v-loading="loading">
          <div class="data-title flex-space-between" slot="header">
            <p>Submissions</p>
            <div class="search-container">
              <el-input
                v-model="quest"
                placeholder="Search by Quest ID or Name"
                clearable
                @input="handleQuestChange"
                class="quest-search-input"
              ></el-input>
            </div>
            <el-select
              v-model="verificationStatusFilter"
              placeholder="All Verification Status"
              clearable
              @change="handleVerificationStatusChange"
              class="verification-filter"
            >
              <el-option label="All" value=""></el-option>
              <el-option label="Manual Review" :value="1"></el-option>
              <el-option label="Completed" :value="2"></el-option>
              <el-option label="Rejected" :value="3"></el-option>
              <el-option label="AI Verified" :value="4"></el-option>
              <el-option label="API Verified" :value="5"></el-option>
            </el-select>
          </div>
          <el-table 
            :data="tableData"
            :default-sort="{ prop: order_column, order: order_type }"
            @sort-change="sort"
            class="w100"
            style="width: 100%"
            stripe
          >
            <el-table-column prop="id" label="ID" sortable></el-table-column>
            <el-table-column label="Task" sortable>
              <template slot-scope="scope">
                {{ scope.row.task ? scope.row.task.name : '' }}
              </template>
            </el-table-column>
            <el-table-column label="User" sortable>
              <template slot-scope="scope">
                {{ scope.row.user ? scope.row.user.email : '' }}
              </template>
            </el-table-column>
            <el-table-column label="Quest" sortable>
              <template slot-scope="scope">
                {{ scope.row.quest ? scope.row.quest.name : '' }}
              </template>
            </el-table-column>
            <el-table-column prop="status" label="Status" sortable>
              <template slot-scope="scope">
                <el-tag :type="getStatusType(scope.row.status)">{{ getStatusText(scope.row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Verified By" sortable>
              <template slot-scope="scope">
                <el-tag v-if="scope.row.verification_type === 2" type="danger">AI</el-tag>
                <el-tag v-if="scope.row.verification_type === 1" type="info">API</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="submitted_at" label="Submitted At" sortable>
              <template slot-scope="scope">
                {{ scope.row.submitted_at | datetime }}
              </template>
            </el-table-column>
            <el-table-column label="Actions" align="center" width="120">
              <template slot-scope="scope">
                <div style="display: flex; justify-content: center;">
                  <el-tooltip v-if="scope.row.image_verification" content="Verification" placement="top">
                    <el-button type="info" circle size="small" icon="el-icon-success" @click="handleVerification(scope.row)"></el-button>
                  </el-tooltip>
                  <el-tooltip content="View" placement="top">
                    <el-button type="primary" circle size="small" icon="el-icon-view" @click="handleView(scope.row)"></el-button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            class="float-right mt20"
            background
            @size-change="sizeChange"
            @current-change="currentChange"
            :current-page.sync="page"
            :page-sizes="[5, 10, 25, 50, 100]"
            :page-size="page_size"
            :page-count="page_count"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
          ></el-pagination>
        </el-card>
      </el-col>
    </el-row>

    <!-- Submission Details Modal -->
    <el-dialog
      title="Submission Details"
      :visible.sync="viewDialogVisible"
      width="70%"
      :before-close="handleCloseViewDialog"
    >
      <div v-if="selectedSubmission">
        <h3 class="section-title">Basic Info</h3>
        <div class="info-grid">
          <div class="grid-item">
            <span class="label">ID:</span>
            <span class="value">{{ selectedSubmission.id }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Submitted At:</span>
            <span class="value">{{ selectedSubmission.submitted_at | datetime }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Status:</span>
            <span class="value">
              <el-tag :type="getStatusType(selectedSubmission.status)">{{ getStatusText(selectedSubmission.status) }}</el-tag>
              <span v-if="selectedSubmission.completed_at" class="ml10">Completed at: {{ selectedSubmission.completed_at | datetime }}</span>
            </span>
          </div>
          <div class="grid-item">
            <span class="label">Created At:</span>
            <span class="value">{{ selectedSubmission.created_at | datetime }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Updated At:</span>
            <span class="value">{{ selectedSubmission.updated_at | datetime }}</span>
          </div>
          <div class="grid-item" v-if="selectedSubmission.completed_at">
            <span class="label">Completed At:</span>
            <span class="value">{{ selectedSubmission.completed_at | datetime }}</span>
          </div>
        </div>

        <el-divider content-position="left">Task Information</el-divider>
        <div class="info-grid" v-if="selectedSubmission.task">
          <div class="grid-item">
            <span class="label">Task ID:</span>
            <span class="value">{{ selectedSubmission.task.id }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Task Name:</span>
            <span class="value">{{ selectedSubmission.task.name }}</span>
          </div>
          <div class="grid-item" v-if="selectedSubmission.task.task_type">
            <span class="label">Task Type:</span>
            <span class="value">{{ selectedSubmission.task.task_type.name }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Reward XP:</span>
            <span class="value">{{ selectedSubmission.task.reward_xp }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Reward Fiat:</span>
            <span class="value">{{ selectedSubmission.task.reward_fiat }}</span>
          </div>
          <div class="grid-item" v-if="selectedSubmission.task.target_username">
            <span class="label">Target Username:</span>
            <span class="value">{{ selectedSubmission.task.target_username }}</span>
          </div>
          <div class="grid-item" v-if="selectedSubmission.task.target_url">
            <span class="label">Target URL:</span>
            <span class="value">
              <a :href="selectedSubmission.task.target_url" target="_blank">{{ selectedSubmission.task.target_url }}</a>
            </span>
          </div>
          <div class="grid-item">
            <span class="label">Verification Required:</span>
            <span class="value">{{ selectedSubmission.task.verification_required ? 'Yes' : 'No' }}</span>
          </div>
        </div>

        <el-divider content-position="left">Quest Information</el-divider>
        <div class="info-grid" v-if="selectedSubmission.quest">
          <div class="grid-item">
            <span class="label">Quest ID:</span>
            <span class="value">{{ selectedSubmission.quest.id }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Quest Name:</span>
            <span class="value">{{ selectedSubmission.quest.name }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Budget:</span>
            <span class="value">{{ selectedSubmission.quest.budget }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Remaining Budget:</span>
            <span class="value">{{ selectedSubmission.quest.remaining_budget }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Start Date:</span>
            <span class="value">{{ selectedSubmission.quest.start_date | datetime }}</span>
          </div>
          <div class="grid-item">
            <span class="label">End Date:</span>
            <span class="value">{{ selectedSubmission.quest.end_date | datetime }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Status:</span>
            <span class="value">{{ selectedSubmission.quest.status }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Published:</span>
            <span class="value">{{ selectedSubmission.quest.is_published ? 'Yes' : 'No' }}</span>
          </div>
        </div>

        <el-divider content-position="left">User Information</el-divider>
        <div class="info-grid" v-if="selectedSubmission.user">
          <div class="grid-item">
            <span class="label">User ID:</span>
            <span class="value">{{ selectedSubmission.user.id }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Username:</span>
            <span class="value">{{ selectedSubmission.user.username }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Name:</span>
            <span class="value">{{ selectedSubmission.user.first_name }} {{ selectedSubmission.user.surname }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Email:</span>
            <span class="value">{{ selectedSubmission.user.email }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Country:</span>
            <span class="value">{{ selectedSubmission.user.country_of_residence }}</span>
          </div>
          <div class="grid-item">
            <span class="label">XP:</span>
            <span class="value">{{ selectedSubmission.user.xp }}</span>
          </div>
          <div class="grid-item" v-if="selectedSubmission.user.experience">
            <span class="label">Level:</span>
            <span class="value">{{ selectedSubmission.user.experience.level }}</span>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleCloseViewDialog">Close</el-button>
      </span>
    </el-dialog>

    <VerificationDialog />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import VerificationDialog from '../components/VerificationDialog.vue'

export default {
  data() {
    return {
      viewDialogVisible: false,
      selectedSubmission: null,
      questSearchTimer: null
    }
  },

  components: {
    VerificationDialog
  },

  computed: {
    ...mapState('submissions', [
      'submissions',
      'loading',
      'page',
      'page_size',
      'total',
      'page_count',
      'order_column',
      'order_type',
      'verificationStatusFilter',
      'quest'
    ]),

    tableData() {
      return this.submissions
    }
  },

  methods: {
    ...mapActions('submissions', ['read', 'manualVerify']),
    ...mapMutations('submissions', [
      'setPage',
      'setPageSize',
      'setOrdering',
      'setVerificationDialog',
      'setSelectedSubmission',
      'setVerificationStatusFilter',
      'setQuest'
    ]),

    sizeChange(page_size) {
      this.setPageSize(page_size)
      this.read()
    },

    currentChange(page) {
      this.setPage(page)
      this.read()
    },

    sort(d) {
      this.setOrdering({
        column: d.prop,
        type: d.order === 'ascending' ? 'asc' : 'desc'
      })
      this.read()
    },

    getStatusType(status) {
      switch (status) {
        case 2:
          return 'success'
        case 1:
          return 'warning'
        case 3:
          return 'danger'
        default:
          return 'info'
      }
    },

    handleVerification(row) {
      this.setSelectedSubmission(row)
      this.setVerificationDialog(true)
    },

    getStatusText(status) {
      switch (status) {
        case 3:
          return 'Rejected'
        case 2:
          return 'Done'
        case 1:
          return 'To be Verified'
        case 0:
          return 'Started'
        default:
          return 'Unknown'
      }
    },

    getRewardStatusType(status) {
      switch (status) {
        case 1:
          return 'success'
        case 0:
          return 'warning'
        default:
          return 'info'
      }
    },

    getRewardStatusText(status) {
      switch (status) {
        case 1:
          return 'Rewarded'
        case 0:
          return 'Pending'
        default:
          return 'Unknown'
      }
    },

    handleView(row) {
      this.selectedSubmission = row;
      this.viewDialogVisible = true;
    },

    handleCloseViewDialog() {
      this.viewDialogVisible = false;
      this.selectedSubmission = null;
    },

    handleVerificationStatusChange(value) {
      this.setVerificationStatusFilter(value)
      this.setPage(1)
      this.read()
    },

    handleQuestChange(value) {
      if (this.questSearchTimer) {
        clearTimeout(this.questSearchTimer)
      }

      this.setQuest(value)

      this.questSearchTimer = setTimeout(() => {
        this.setPage(1)
        this.read()
      }, 500)
    },

    handleManualVerification() {
      this.$confirm('Are you sure?', 'Manual Verification', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.manualVerify(this.selectedSubmission.id)
          .then(() => {
            this.$message({
              type: 'success',
              message: 'Submission has been manually verified'
            })
            this.viewDialogVisible = false
            this.selectedSubmission = null
          })
          .catch(error => {
            this.$message({
              type: 'error',
              message: 'Failed to manually verify submission'
            })
          })
      }).catch(() => {
        this.$message({
          type: 'info',
          message: 'Manual verification canceled'
        })
      })
    },

    handleEdit(row) {
      // Placeholder for edit action
      console.log('Edit submission:', row)
      this.$message({
        message: 'Edit functionality will be implemented in the future',
        type: 'info'
      })
    },

    handleDelete(row) {
      this.$confirm('Are you sure you want to delete this submission?', 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        // Placeholder for delete action
        console.log('Delete submission:', row)
        this.$message({
          type: 'success',
          message: 'Delete functionality will be implemented in the future'
        })
      }).catch(() => {
        this.$message({
          type: 'info',
          message: 'Delete canceled'
        })
      })
    }
  },

  created() {
    this.read()
  },

  beforeRouteLeave(to, from, next) {
    this.$store.commit('submissions/reset')
    next()
  }
}
</script>

<style scoped>
.data-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mr5 {
  margin-right: 5px;
}

.mt20 {
  margin-top: 20px;
}

.float-right {
  float: right;
}

.w100 {
  width: 100%;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 15px;
  background-color: #fff;
}

.grid-item {
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
}

.label {
  font-weight: bold;
  margin-right: 8px;
  min-width: 120px;
}

.value {
  font-weight: normal;
}

.section-title {
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.ml10 {
  margin-left: 10px;
}
.manual-verify {
  padding: 5px;
}

.search-container {
  display: flex;
  gap: 10px;
  align-items: center;
  flex: 1;
  justify-content: center;
  margin: 0 20px;
}

.quest-search-input {
  width: 300px;
}

.verification-filter {
  width: 200px;
}
</style>
